import React, { useState, useEffect } from 'react';
import CustomerCard from '../components/mobile/Customer';
import CustomerForm from '../components/form/Customer';
import CustomerHeader from '../components/common/Customer';
import CustomerTable from '../components/desktop/Customer';
import Pagination from '../components/Pagination';
import Spinner from '../components/ui/Spinner';
import { getBranches } from '../services/branches';
import { createCustomer, getCustomers, toggleCustomerStatus, updateCustomerPartial } from '../services/customers';
import { getClients } from '../services/clients';
import ConfirmationModal from '../components/modals/ConfirmationModal';
import { USER_PROFILE, USER_ROLE } from '../services/auth';

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [clients, setClients] = useState([]);
  const [count, setCount] = useState(0);
  const [errors, setErrors] = useState([]);
  const [branches, setBranches] = useState([]);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [initialData, setInitialData] = useState({});
  const [showForm, setShowForm] = useState(false);
  const [search, setSearch] = useState('');
  const [inputSearch, setInputSearch] = useState('');
  const [branchFilter, setBranchFilter] = useState(0);
  const [statusFilter, setStatusFilter] = useState('Active');
  const [currentPage, setCurrentPage] = useState(1);
  const [pendingPage, setPendingPage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const [confirmModalParams, setConfirmModalParams] = useState({
    open: false,
    title: 'Are you sure?',
    message: 'Do you really want to proceed with this action?',
    confirmText: 'Yes, Do it',
    cancelText: 'No, Cancel',
  });

  const role = localStorage.getItem(USER_ROLE);
  const showBranches = (role == 'admin');
  const customersPerPage = 20;

  useEffect(() => {
    setPendingPage(1);
  }, [search, branchFilter, statusFilter]);

  useEffect(() => {
    if (pendingPage != null) {
      fetchCustomers(pendingPage);
    }
    if (clients && clients.length == 0) {
      fetchClients();
    }
    const user = localStorage.getItem(USER_ROLE);
    if (user == 'admin' && branches.length == 0) {
      fetchBranches();
    }
  }, [pendingPage]);

  const fetchClients = async () => {
    setLoading(true);
    await getClients({paginate: false})
      .then(([err, data]) => {
        setClients(data || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const fetchCustomers = async (page) => {
    setLoading(true);
    let branch = 0;
    if (showBranches) {
      branch = branchFilter;
    } else {
      const user = JSON.parse(localStorage.getItem(USER_PROFILE));
      branch = user.branch;
    }
    const params = {
      page, search, branch,
      status: statusFilter.toLowerCase(),
      pageSize:customersPerPage
    };
    await getCustomers(params)
      .then(([err, data]) => {
        const rows = Array.isArray(data?.results) ? data.results : [];
        const normalizedFilter = (statusFilter || 'all').toLowerCase();
        const filteredRows = normalizedFilter === 'active'
          ? rows.filter((item) => Boolean(item?.is_active))
          : normalizedFilter === 'inactive'
          ? rows.filter((item) => !Boolean(item?.is_active))
          : rows;
        setCustomers(filteredRows);
        setCount(data.count);
        setCurrentPage(page);
        setPendingPage(null);
        setLoading(false);
      })
      .catch((error) => {
        setPendingPage(null);
        setLoading(false);
      });
  };

  const fetchBranches = async () => {
    setLoading(true);
    await getBranches({ paginate: false })
      .then(([err, data]) => {
        setBranches(data || []);
        setLoading(false);
      })
      .catch((error) => {
        setLoading(false);
      });
  };

  const onBranchFilter = (e) => {
    setBranchFilter(branches[e.target.selectedIndex-1]?.id);
  };

  const onStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleAdd = () => {
    setEditingCustomer(null);
    setInitialData({});
    setShowForm(true);
    setErrors([]);
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setInitialData(JSON.parse(JSON.stringify(customer)));
    setShowForm(true);
    setErrors([]);
  };

  const getChangedFields = (initial, updated) => {
    const changed = {};
    const excludes = ["custom_fields", "mis_fields"];
    for (const key in updated) {
      if (excludes.includes(key)) {
        changed[key] = updated[key];
      }
      if (Array.isArray(updated[key])) {
        if (JSON.stringify(initial[key]) !== JSON.stringify(updated[key])) {
          changed[key] = updated[key];
        }
      } else if (updated[key] !== initial[key]) {
        changed[key] = updated[key];
      }
    }
    return changed;
  };

  const handleSave = async (customerData) => {
    let err, response;
    setLoading(true);
    if (editingCustomer) {
      const changedFields = getChangedFields(initialData, customerData);
      if (Object.keys(changedFields).length === 0) {
        return;
      }
      [err, response] = await updateCustomerPartial(customerData.id, changedFields);
      if (!err) {
        setInitialData({});
      }
    } else {
      customerData.is_active = 1;
      [err, response] = await createCustomer(customerData);
    }
    setLoading(false);
    if (err) {
      setErrors(err);
    } else {
      await fetchCustomers(currentPage);
      setShowForm(false);
    }
  };

  const handleToggleStatus = async (customer) => {
    setConfirmModalParams({
      ...confirmModalParams,
      open: true,
      title: customer.is_active ? 'Deactivate Customer' : 'Activate Customer',
      message: `Are you sure you want to ${customer.is_active ? 'deactivate' : 'activate'} the customer "${customer.name}"?`,
      onConfirm: () => handleAction(customer),
    });
  };

  const handleAction = async (customer) => {
    setLoading(true);
    await toggleCustomerStatus(customer.id, !customer.is_active);
    setConfirmModalParams({...confirmModalParams, open: false});
    await fetchCustomers(currentPage);
    setLoading(false);
  };

  const onDownload = async (customer) => {
    const fileUrl = customer['sla_contract'];
    const filename = fileUrl.split('/').pop();
    const response = await fetch(fileUrl);
    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    setSearch(inputSearch.trim());
  };

  const onSearchTextChange = (e) => {
    setInputSearch(e.target.value)
  };

  const onToggleStatus = (idx) => {
    handleToggleStatus(customers[idx]);
  };

  const onExpandCollpase = (idx) => {
    setExpandedIndex(expandedIndex === idx ? null : idx);
  };

  const onClearSearch = () => {
    setInputSearch('');
    setSearch('');
    setCurrentPage(1);
    setPendingPage(1);
  };

  const handleCustomerSelection = (customer) => {
    handleEdit(customer);
  };

  const handleCancel = async () => {
    setShowForm(false);
  };

  return (
    <div className="p-4 w-full">
      <CustomerHeader
        inputSearch={inputSearch}
        count={count}
        branchFilter={branchFilter}
        branches={branches}
        onBranchFilter={onBranchFilter}
        onSearchSubmit={onSearchSubmit}
        onSearchTextChange={onSearchTextChange}
        handleAdd={handleAdd}
        onClearSearch={onClearSearch}
        statusFilter={statusFilter}
        onStatusFilter={onStatusFilter}
        onBack={handleCancel}
        customer={editingCustomer}
        showForm={showForm}
      />

      {showForm ? (
        <CustomerForm
          customer={editingCustomer}
          dataErrors={errors}
          onSave={handleSave}
          onCancel={handleCancel}
          handleDownloadFromServer={onDownload}
        />
      ) : (
        <>
          {loading ? <Spinner /> : (
            <>
              <CustomerTable
                customers={customers}
                clients={clients}
                expandedIndex={expandedIndex}
                onToggleStatus={onToggleStatus}
                onExpandCollpase={onExpandCollpase}
                handleEdit={handleEdit}
                handleDownload={onDownload}
                onCustomerSelected={handleCustomerSelection}
              />

              <CustomerCard
                customers={customers}
                onToggleStatus={onToggleStatus}
                handleEdit={handleEdit}
                handleDownload={onDownload}
              />
            </>
          )}
          

          <div className="md:static fixed bottom-0 left-0 w-full bg-white border-t shadow z-10">
            {customers.length > 0 && (
              <Pagination
                currentPage={currentPage}
                totalPages={Math.max(1, Math.ceil(count / customersPerPage))}
                onPageChange={setPendingPage}
              />
            )}
          </div>
        </>
      )}
      <ConfirmationModal
        open={confirmModalParams.open}
        title={confirmModalParams.title}
        message={confirmModalParams.message}
        onConfirm={confirmModalParams.onConfirm}
        onCancel={() => setConfirmModalParams({...confirmModalParams, open: false})}
        confirmText={confirmModalParams.confirmText}
        cancelText={confirmModalParams.cancelText}
      />
    </div>
  );
};

export default Customers;
